import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import PuffLoader from 'react-spinners/PuffLoader';
import {useTranslation} from 'react-i18next';

import {theme}   from '../constants';
import {hooks}   from '../hooks';
import {components} from '../components';
import axiosInstance from '../api/axiosInstance';
import {getDeviceFingerprint} from '../utils/fingerprint';
import {saveHotelContext} from '../hooks/useAuth';

type AuthState = 'loading' | 'success' | 'device_conflict' | 'expired' | 'error';

export const Auth: React.FC = () => {
  const {t} = useTranslation();
  const [searchParams]   = useSearchParams();
  const navigate          = hooks.useAppNavigate();
  const [state, setState] = useState<AuthState>('loading');

  useEffect(() => {
    let cancelled = false;

    async function bind() {
      const token = searchParams.get('token');
      if (!token) { setState('expired'); return; }

      try {
        const fingerprint = await getDeviceFingerprint();
        const res = await axiosInstance.post('/guest-auth/bind', {token, fingerprint});
        if (cancelled) return;

        const guestData = res.data?.data ?? res.data;
        const {accessToken, hotelId, hotelName, hotelSlug, ...profile} = guestData;
        localStorage.setItem('guest_token',   accessToken);
        localStorage.setItem('guest_profile', JSON.stringify(profile));
        // Persist hotel context so every screen can show the correct hotel branding
        saveHotelContext(hotelId, hotelName, hotelSlug);

        setState('success');
        setTimeout(() => navigate('/'), 1000);
      } catch (err: any) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 403)      setState('device_conflict');
        else if (status === 401) setState('expired');
        else                     setState('error');
      }
    }

    bind();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.ghostWhite,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px clamp(20px, 6vw, 32px)',
    textAlign: 'center',
  };

  const statusCard = (
    dotColor: string,
    headline: string,
    body: string,
    extra?: React.ReactNode,
  ) => (
    <div style={page}>
      <components.LanguageSwitcher
        containerStyle={{position: 'fixed', top: 16, insetInlineEnd: 16, zIndex: 10}}
      />

      <img src='/logo.webp' alt='Logo' style={{height: 30, marginBottom: 48, opacity: 0.7}} />

      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 6,
        border: `1px solid ${dotColor}30`,
        backgroundColor: `${dotColor}0A`,
        marginBottom: 20,
      }}>
        <div style={{width: 7, height: 7, borderRadius: '50%', backgroundColor: dotColor}} />
        <span style={{fontSize: 11, fontWeight: 600, color: dotColor, fontFamily: "'SS3', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase'}}>
          {headline}
        </span>
      </div>

      <div style={{
        fontSize: 14,
        color: theme.colors.bodyTextColor,
        fontFamily: "'SS3', sans-serif",
        lineHeight: 1.7,
        maxWidth: 300,
      }}>
        {body}
      </div>

      {extra}
    </div>
  );

  if (state === 'loading') {
    return (
      <div style={page}>
        <components.LanguageSwitcher
          containerStyle={{position: 'fixed', top: 16, insetInlineEnd: 16, zIndex: 10}}
        />

        <img src='/logo.webp' alt='Logo' style={{height: 30, marginBottom: 48, opacity: 0.7}} />

        <PuffLoader size={32} color={theme.colors.mainColor} speedMultiplier={0.9} />
        <div style={{
          marginTop: 16,
          fontSize: 14,
          color: theme.colors.bodyTextColor,
          fontFamily: "'SS3', sans-serif",
        }}>
          {t('auth.verifyingAccess')}
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return statusCard(
      '#1A9B6C',
      t('auth.welcome'),
      t('auth.takingToAccount'),
    );
  }

  if (state === 'device_conflict') {
    return statusCard(
      '#2F72C8',
      t('auth.alreadyLinked'),
      t('auth.deviceConflict'),
    );
  }

  if (state === 'expired') {
    return statusCard(
      theme.colors.mainColor,
      t('auth.accessExpired'),
      t('auth.accessExpiredBody'),
    );
  }

  return statusCard(
    theme.colors.dangerRed,
    t('auth.somethingWentWrong'),
    t('auth.genericError'),
  );
};
